import Cookies from 'js-cookie';

export interface UtmParamConfigItem {
  name: string;
  required: boolean;
}

export const JUMP_ID_PARAM = 'jumpid';
export const DEFAULT_UTM_PARAMS_CONFIG: UtmParamConfigItem[] = [
  {name: 'utm_source', required: true},
  {name: 'utm_medium', required: true},
  {name: 'utm_campaign', required: true},
  {name: 'utm_content', required: true},
  {name: 'utm_term', required: false},
  {name: 'utm_geo', required: false}
];

export const validateUtmSet = (utmParams: Record<string, any>, utmParamsConfig: UtmParamConfigItem[]) => {
  return utmParamsConfig.every((param) => {
    const paramValue = utmParams[param.name];
    const validValue = paramValue && !paramValue.toLowerCase().includes(`{my.${param.name}}`);
    return (!param.required || validValue);
  });
};

export const getUtmUrlParams = (utmParamsConfig: UtmParamConfigItem[]): Record<string, any> => {
  const {searchParams: usp} = new URL(window.location.href);
  const utmParams: Record<string, any> = {};

  utmParamsConfig.forEach((param) => {
    const value = usp.get(param.name);
    utmParams[param.name] = value || '';
  });

  return utmParams;
};

export const getUtmCookieParams = (utmParamsConfig: UtmParamConfigItem[]): Record<string, any> => {
  const utmParams: Record<string, any> = {};

  utmParamsConfig.forEach((param) => {
    const value = Cookies.get(param.name);
    utmParams[param.name] = value || '';
  });

  return utmParams;
};

/**
 * Looks for required based on config params in url.
 * If any of required params not found in url, looks available params in cookies.
 * If nothing found in cookies, returns empty object.
 * @param utmParamsConfig - config for utm params {@link UtmParamConfigItem}
 */
export const getUtmParams = (utmParamsConfig: UtmParamConfigItem[]): Record<string, any> => {
  utmParamsConfig = utmParamsConfig.length > 0 ? utmParamsConfig : DEFAULT_UTM_PARAMS_CONFIG;
  const urlParams = getUtmUrlParams(utmParamsConfig);

  if (validateUtmSet(urlParams, utmParamsConfig)) {
    return urlParams;
  }

  return getUtmCookieParams(utmParamsConfig);
};

export const getJumpId = () => {
  const {searchParams: usp} = new URL(window.location.href);

  return usp.get(JUMP_ID_PARAM);
};

export const initFormMetricsMixin = async (): Promise<void> => {
  const {HPEFormMetricsMixin} = await import(/* webpackChunkName: "hpe-forms/metrics/hpe-form-metrics-mixin"*/'hpe-forms/metrics/hpe-form-metrics-mixin');
  HPEFormMetricsMixin.register();
};

export const initHPEFormItem = async (): Promise<void> => {
  const {HPEFormItem} = await import(/* webpackChunkName: "hpe-forms/hpe-form-item"*/'hpe-forms/hpe-form-item');
  HPEFormItem.register();
};

export const initHPEFormWrapper = async (): Promise<void> => {
  const {HPEFormWrapper} = await import(/* webpackChunkName: "hpe-forms/hpe-form-wrapper"*/'hpe-forms/hpe-form-wrapper');
  HPEFormWrapper.register();
};

export const initHPEFormSelect = async (): Promise<void> => {
  const [
    eslSelectListModule,
    eslSelectModule
  ] = await Promise.all([
    import(/* webpackChunkName: "esl-select-list"*/'@exadel/esl/modules/esl-forms/esl-select-list/core'),
    import(/* webpackChunkName: "esl-select"*/'@exadel/esl/modules/esl-forms/esl-select/core')
  ]);

  eslSelectListModule.ESLSelectList.register();
  eslSelectModule.ESLSelect.register();
};

export const initHPEForm = async (): Promise<void> => {
  await Promise.all([
    initHPEFormItem(),
    initHPEFormWrapper(),
    initHPEFormSelect()
  ]);
};
